const DB_NAME = "AudioDB"
const STORE_NAME = "audioFiles"

// Opens a connection to the IndexedDB database
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, 1)

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db: IDBDatabase = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" })
      }
    }

    request.onsuccess = (event: Event) => {
      const db: IDBDatabase = (event.target as IDBOpenDBRequest).result
      resolve(db)
    }

    request.onerror = (event: Event) => {
      reject((event.target as IDBOpenDBRequest).error)
    }
  })
}

// Function to store an audio file in the database
async function storeFile(filename: string, content: Blob): Promise<void> {
  const db: IDBDatabase = await openDB()
  const tx: IDBTransaction = db.transaction(STORE_NAME, "readwrite")
  const store: IDBObjectStore = tx.objectStore(STORE_NAME)

  await new Promise<void>((resolve, reject) => {
    const request = store.put({ id: filename, content })
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

// Function to retrieve an audio file from the database by its filename
async function getAudioFileFromDB(filename: string): Promise<Blob> {
  const db: IDBDatabase = await openDB()
  const tx: IDBTransaction = db.transaction(STORE_NAME, "readonly")
  const store: IDBObjectStore = tx.objectStore(STORE_NAME)

  return new Promise<Blob>((resolve, reject) => {
    const request = store.get(filename)
    request.onsuccess = () => {
      if (!request.result) {
        reject(new Error(`File not found: ${filename}`))
      } else {
        resolve(request.result.content)
      }
    }
    request.onerror = () => {
      reject(request.error)
    }
  })
}

// Function to clear all audio files from the database
async function clearAudioFiles(): Promise<void> {
  const db: IDBDatabase = await openDB()
  const tx: IDBTransaction = db.transaction(STORE_NAME, "readwrite")
  const store: IDBObjectStore = tx.objectStore(STORE_NAME)

  await new Promise<void>((resolve, reject) => {
    const request = store.clear()
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

export { openDB, storeFile, getAudioFileFromDB, clearAudioFiles }
